/**
 * Hook to generate button hint labels based on the last used controller device.
 * Returns appropriate button labels (e.g., "A", "Enter", "Space") for displaying in UI based on whether
 * keyboard or gamepad was last used, helping users understand which buttons to press.
 */
import { useMemo } from 'react'
import { useFocusNavigationDep, useControllerInputDep } from '../components/providers/FocusNavigationProvider/FocusNavigationProvider'
import { useSignals } from '@preact/signals-react/runtime'
import { getKeyboardProfile } from '../controllers/profiles/registry'
import type { ControllerProfile, KeyboardProfile } from '../controllers/profiles/types'

/**
 * Button hint labels for different controller types
 */
export interface ButtonHints {
  /** Select/Activate button label (A on Xbox, Enter/Space on keyboard) */
  select: string
  /** Cancel button label (B on Xbox, Escape on keyboard) */
  cancel: string
  /** Exit button label (B on Xbox, Escape on keyboard) */
  exit: string
  /** Menu button label (Menu on Xbox, M on keyboard) */
  menu: string
}

/**
 * Hook to get button hints based on the active input method.
 * Returns button labels that should be shown to users (e.g., "A", "B", "Enter", "Escape").
 * 
 * Currently uses keyboard profile as the primary source since we don't have
 * a way to detect which controller is actively being used.
 * In the future, this could be enhanced to detect the active controller.
 */
export function useControllerButtonHints(): ButtonHints {
  useSignals()
  const focusNavigation = useFocusNavigationDep()
  const controllerInput = useControllerInputDep()
  const isActive = focusNavigation.isActive.value
  
  // Read signal values outside useMemo so they can be included in dependency array
  const lastUsedDevice = controllerInput.lastUsedDevice.value
  const lastUsedGamepadProfile = controllerInput.lastUsedGamepadProfile.value
  
  return useMemo(() => {
    if (!isActive) {
      // Return empty hints when focus navigation is not active
      return {
        select: '',
        cancel: '',
        exit: '',
        menu: '',
      }
    }

    // Use last used device to determine hints
    // If gamepad was used last, use gamepad profile; otherwise use keyboard
    if (lastUsedDevice === 'gamepad' && lastUsedGamepadProfile) {
      return getButtonHintsForProfile(lastUsedGamepadProfile)
    }

    // Default to keyboard profile (even if no device has been used yet)
    const keyboardProfile = getKeyboardProfile()
    return getButtonHintsForProfile(keyboardProfile)
  }, [isActive, lastUsedDevice, lastUsedGamepadProfile])
}

/**
 * Get button hints for a specific controller profile.
 * Useful when you know which controller is being used.
 */
export function getButtonHintsForProfile(profile: ControllerProfile | KeyboardProfile): ButtonHints {
  if (profile.id === 'keyboard') {
    const keyboardProfile = profile as KeyboardProfile
    const getKeyLabel = (key: string): string => {
      switch (key) {
        case 'Enter':
          return 'Enter'
        case 'Space':
          return 'Space'
        case 'Escape':
          return 'Esc'
        case 'M':
          return 'M'
        default:
          return key
      }
    }
    return {
      select: getKeyLabel(keyboardProfile.buttons.select),
      cancel: getKeyLabel(keyboardProfile.buttons.exit),
      exit: getKeyLabel(keyboardProfile.buttons.exit),
      menu: getKeyLabel(keyboardProfile.buttons.menu),
    }
  }

  // For gamepad profiles, use standard button names
  const controllerProfile = profile as ControllerProfile
  const buttonNames: Record<string, { select: string; exit: string; menu: string }> = {
    'xbox': {
      select: 'A',
      exit: 'B',
      menu: 'Menu',
    },
    'generic': {
      // For generic controllers, try to infer from common mappings
      // Most controllers follow Xbox layout
      select: 'A',
      exit: 'B',
      menu: 'Menu',
    },
  }

  const names = buttonNames[controllerProfile.id] || buttonNames['generic']!

  return {
    select: names.select,
    cancel: names.exit, // cancel is an alias for exit
    exit: names.exit,
    menu: names.menu,
  }
}
